import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Trash2 } from "lucide-react";

import AdminLayout from "@/pages/admin/AdminLayout";
import { adminListChecks, adminDeleteCheck, adminBulkDelete } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function AdminChecks() {
  const { toast } = useToast();
  const [checks, setChecks] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    adminListChecks({ limit: 200 })
      .then((d) => setChecks(d.checks || []))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function toggle(id) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }

  async function removeOne(id) {
    if (!window.confirm("Verwijder deze analyse? De bijbehorende betaling, credits en refund-records worden ook gewist uit /admin/revenue.")) return;
    const res = await adminDeleteCheck(id);
    const ex = res?.deleted_extras || {};
    toast({
      title: "Analyse verwijderd",
      description: `Ook opgeruimd: ${ex.payment || 0} betaling · ${ex.bundle || 0} bundle · ${ex.credits || 0} credits · ${ex.refunds || 0} refunds`,
    });
    load();
  }

  async function bulkRemove() {
    if (selected.size === 0) return;
    if (!window.confirm(`Verwijder ${selected.size} analyses? De bijbehorende betalingen, bundles, credits en refund-records worden ook gewist uit /admin/revenue.`)) return;
    const res = await adminBulkDelete(Array.from(selected));
    const ex = res?.deleted_extras || {};
    toast({
      title: `${res.deleted} analyses verwijderd`,
      description: `Ook opgeruimd: ${ex.payments || 0} betalingen · ${ex.bundles || 0} bundles · ${ex.credits || 0} credits · ${ex.refunds || 0} refunds`,
    });
    setSelected(new Set());
    load();
  }

  return (
    <AdminLayout>
      <div data-testid="admin-checks-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink tracking-tight">Analyses</h1>
            <p className="text-ink-soft mt-1 text-sm">{checks.length} resultaten</p>
          </div>
          {selected.size > 0 && (
            <button
              onClick={bulkRemove}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600 hover:bg-red-700 text-white text-sm font-semibold"
              data-testid="admin-bulk-delete"
            >
              <Trash2 className="h-4 w-4" /> Verwijder {selected.size} geselecteerd
            </button>
          )}
        </div>

        <div className="mt-6 rounded-2xl bg-white border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-ink-soft text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left w-10"></th>
                <th className="px-4 py-3 text-left">Checker</th>
                <th className="px-4 py-3 text-left">Score</th>
                <th className="px-4 py-3 text-left">E-mail</th>
                <th className="px-4 py-3 text-left">Aangemaakt</th>
                <th className="px-4 py-3 text-right">Actie</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">Laden…</td></tr>
              ) : checks.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-soft">Geen analyses gevonden.</td></tr>
              ) : (
                checks.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-secondary/50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggle(c.id)}
                        data-testid={`select-${c.id.slice(0, 6)}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-ink">{c.checker_name}</div>
                      <div className="text-xs text-ink-subtle">{c.checker_slug}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="font-bold"
                        style={{ color: c.score >= 60 ? "#10B981" : c.score >= 40 ? "#F59E0B" : "#EF4444" }}
                      >
                        {c.score}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-ink-soft">{c.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3 text-right space-x-2">
                      <Link
                        to={`/result/${c.id}`}
                        target="_blank"
                        className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-xs"
                      >
                        <ExternalLink className="h-3.5 w-3.5" /> Open
                      </Link>
                      <button
                        onClick={() => removeOne(c.id)}
                        className="inline-flex items-center gap-1 text-red-600 hover:underline text-xs"
                        data-testid={`delete-${c.id.slice(0, 6)}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Wis
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
